import { memo } from 'react';
import { Link } from 'react-router-dom';
import { Comment, Avatar, Popover } from 'antd';
import { LuCornerDownRight } from 'react-icons/lu';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { brightenColor } from 'src/customizable-ui/dashboard/utils/brightenColor.js';
import { CoreBadge } from 'src/RnRCommonComponent';
import {
	REVERSE_MAPING_BADGE_TYPE,
	ICON_COMPLETE_MAP,
} from 'src/RnRCommonComponent/CoreBadge/rewardsConstants';
import {
	getRecognizerName,
	isAutomatedRecognizer,
} from 'src/rewardsAndRecognitionHelper/utils.js';

dayjs.extend(relativeTime);

const getInitials = (user) => {
	const firstNameInitial = user?.firstName
		? user.firstName.charAt(0).toUpperCase()
		: '';
	const lastNameInitial = user?.lastName
		? user.lastName.charAt(0).toUpperCase()
		: '';
	return `${firstNameInitial}${lastNameInitial}`;
};

const RecognitionShoutoutsCard = memo(
	({
		card,
		isEditMode = true,
		isDragging = false,
		isResizing = false,
		isHovered = false,
		recognitionShoutouts = [],
		companyName = '',
	}) => {
		const cardData = card.data || {};
		const title = cardData.title || 'Recognition Shoutouts';
		const backgroundColor = cardData.backgroundColor || '#ffffff';
		const borderColor = cardData.borderColor || '#dddddd';
		const textColor = cardData.textColor || '#333333';
		const showBorder = cardData.showBorder !== false;
		const borderSize = cardData.borderSize || 1;
		const shoutoutsLimit = cardData.shoutoutsLimit || 2;

		const visibleShoutouts = recognitionShoutouts.slice(0, shoutoutsLimit);

		const getCardBorderStyle = () => {
			if (!showBorder && !isEditMode) return 'none';

			if (isDragging || isResizing) {
				return `${borderSize}px solid #74a9ffff`;
			}

			if (isEditMode && isHovered && !isDragging && !isResizing) {
				const brighterBorder = brightenColor(borderColor || '#dddddd', 30);
				return `${borderSize}px solid ${brighterBorder}`;
			}

			if (showBorder) {
				return `${borderSize}px solid ${borderColor}`;
			}

			return 'none';
		};

		const getCardShadow = () => {
			if (isDragging || isResizing) {
				return '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
			}
			if (isEditMode && isHovered && !isDragging && !isResizing) {
				return '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
			}
			return 'none';
		};

		const formatRecipients = (recipients) => {
			if (!recipients || recipients.length === 0) return '';

			if (recipients.length === 1) {
				const recipient = recipients[0];
				return (
					`${recipient.firstName || ''} ${recipient.lastName || ''}`.trim() ||
					'Unknown User'
				);
			}

			if (recipients.length <= 3) {
				return recipients
					.map(
						(recipient) =>
							`${recipient.firstName || ''} ${recipient.lastName || ''}`.trim() ||
							'Unknown User'
					)
					.join(', ');
			}

			const firstThree = recipients
				.slice(0, 3)
				.map(
					(recipient) =>
						`${recipient.firstName || ''} ${recipient.lastName || ''}`.trim() ||
						'Unknown User'
				)
				.join(', ');
			return `${firstThree} and ${recipients.length - 3} others`;
		};

		return (
			<div
				className="w-full h-full flex flex-col relative transition-all duration-200"
				style={{
					background: backgroundColor,
					border: getCardBorderStyle(),
					boxShadow: getCardShadow(),
					transform: isDragging ? 'scale(1.05)' : 'scale(1)',
					borderRadius: '14px',
					padding: '16px',
				}}
			>
				{/* Header */}
				<h4
					style={{
						fontSize: '14px',
						fontWeight: 700,
						lineHeight: '18px',
						margin: '0 0 16px',
						color: textColor,
						fontFamily: 'Nunito Sans, sans-serif',
					}}
				>
					{title}
				</h4>

				{/* Content */}
				<div
					className="flex-1"
					style={{
						overflowY: 'auto',
						overflowX: 'hidden',
						minHeight: 0,
					}}
				>
					<div>
						{visibleShoutouts.length > 0 ? (
							visibleShoutouts.map((item, index) => {
								const messageText = item?.content?.message || '';
								const formattedCreatedAt = dayjs(item?.createdAt).fromNow();

								const badge = item?.badge || {};
								const { label, color, gradientColor } = badge;
								const badgeTypeCode = badge?.badgeTypeId
									? REVERSE_MAPING_BADGE_TYPE[badge.badgeTypeId]
									: null;
								const iconData = badge?.iconId
									? ICON_COMPLETE_MAP.find((icon) => icon.id === badge.iconId)
									: null;
								const iconCode = iconData ? iconData.code : null;

								const mainRecipient = item?.user || item?.accountClaim;

								const recipients = (
									item?.recipients || [
										{
											user: mainRecipient,
										},
									]
								).map((recipient) => ({
									id: recipient?.user?.id || recipient?.accountClaim?.id,
									firstName:
										recipient?.user?.firstName ||
										recipient?.accountClaim?.firstName,
									lastName:
										recipient?.user?.lastName ||
										recipient?.accountClaim?.lastName,
								}));

								const recipientText = formatRecipients(recipients);

								const sentByName = (
									<div style={{ display: 'flex', flexDirection: 'row' }}>
										<div>
											<span>
												{getRecognizerName(
													item?.awardedBy,
													item?.awardedByUser,
													companyName
												)}
											</span>
											<Popover
												trigger="hover"
												content={dayjs(item?.createdAt).format(
													'MMMM D, YYYY h:mm A'
												)}
												getPopupContainer={(trigger) => trigger.parentNode}
											>
												<span
													style={{
														marginLeft: '8px',
														color: '#ccc',
														fontSize: '12px',
														cursor: 'default',
													}}
												>
													{formattedCreatedAt}
												</span>
											</Popover>
											<div
												style={{
													marginLeft: '4px',
													display: 'flex',
													alignItems: 'baseline',
												}}
											>
												<LuCornerDownRight />
												<span style={{ marginLeft: 4 }}>@{recipientText}</span>
											</div>
										</div>
										<div style={{ display: 'inline-flex', marginLeft: '4px' }}>
											{badgeTypeCode && (
												<div
													style={{
														width: '36px',
														height: '36px',
														overflow: 'visible',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														flexShrink: 0,
													}}
												>
													<div
														style={{
															transform: 'scale(0.2)',
															transformOrigin: 'center',
														}}
													>
														<CoreBadge
															useTilt={false}
															badgeType={badgeTypeCode}
															badgeIcon={iconCode}
															label={label}
															color={color}
															gradientColor={gradientColor}
															shape={badge.shape}
															badgeRotation={badge.badgeRotation}
															customImageSignedUrl={badge.customImageSignedUrl}
															labelColor={badge.labelColor}
															customIconSignedUrl={badge.customIconSignedUrl}
														/>
													</div>
												</div>
											)}
										</div>
									</div>
								);

								const content = (
									<div>
										{messageText && (
											<div
												style={{
													wordWrap: 'break-word',
												}}
											>
												{messageText}
											</div>
										)}
									</div>
								);

								return (
									<Link
										key={item.id || index}
										to="/recognition-hub"
										className="block transition-transform duration-200 hover:-translate-y-1"
										style={{
											textDecoration: 'none',
											color: 'inherit',
											marginBottom:
												index < visibleShoutouts.length - 1 ? '16px' : '0',
										}}
									>
										<Comment
											author={sentByName}
											avatar={
												<Avatar>
													{isAutomatedRecognizer(item?.awardedBy)
														? companyName?.[0]
														: getInitials(item?.awardedByUser)}
												</Avatar>
											}
											content={content}
										/>
										{index < visibleShoutouts.length - 1 && (
											<div
												style={{
													height: '0px',
													borderBottom: `1px solid ${borderColor}`,
													margin: '16px 0',
													width: '100%',
												}}
											/>
										)}
									</Link>
								);
							})
						) : (
							<div
								style={{
									padding: '20px',
									textAlign: 'center',
									color: textColor,
									opacity: 0.6,
									fontSize: '13px',
									fontFamily: 'Nunito Sans, sans-serif',
								}}
							>
								No recognition shoutouts yet
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
);

RecognitionShoutoutsCard.displayName = 'RecognitionShoutoutsCard';

export default RecognitionShoutoutsCard;
